export type LeadStage =
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'proposal'
  | 'negotiation'
  | 'won'
  | 'lost';

// Stage attivi (non terminali) nell'ordine in cui appaiono in pipeline.
export const ACTIVE_STAGES: LeadStage[] = [
  'new', 'contacted', 'qualified', 'proposal', 'negotiation',
];

export const TERMINAL_STAGES: LeadStage[] = ['won', 'lost'];

export type SectorType = 'private' | 'public';

// Stessa tassonomia di src/config/sectorClusters.ts (SECTOR_TO_CLUSTER) —
// duplicata qui per restare un'app indipendente, non importata.
export type Sector =
  | 'banking' | 'fiduciary' | 'insurance'
  | 'healthcare'
  | 'legal'
  | 'retail'
  | 'technology'
  | 'education'
  | 'hr' | 'manufacturing' | 'construction' | 'other'
  | 'public_municipality' | 'public_canton' | 'public_hospital' | 'public_school';

// Stessa fascia usata per il pricing (src/config/pricing.ts, EmployeeTier).
export type EmployeeTier = 'micro' | 'small' | 'medium' | 'large';

export type PreferredLanguage = 'it' | 'de' | 'fr' | 'en';

export type LeadSource = 'fiera' | 'referral' | 'cold_outreach' | 'linkedin' | 'passaparola' | 'agente_registro' | 'checkup_web' | 'price_estimate_web' | 'sito_web' | 'altro';

export type EstimatedTimeline = '<1m' | '1-3m' | '3-6m' | '>6m';

export type LostReason = 'budget' | 'timing' | 'competitor' | 'no_urgency' | 'ghosting' | 'out_of_scope' | 'altro';

export interface StageHistoryEntry {
  stage: LeadStage;
  enteredAt: unknown; // data ISO
}

export interface Lead {
  id: string;

  // Azienda/Ente
  organizationName: string;
  sectorType: SectorType;
  sector: Sector;
  /** Assente per i lead trovati dall'agente di sourcing (registro di
   * commercio non riporta il numero di dipendenti) — da confermare quando
   * un umano completa la scheda. */
  employeeTier?: EmployeeTier;
  canton?: string;
  /** Comune, CAP, via — presenti solo per i lead trovati dall'agente di
   * sourcing quando Zefix li forniva (v. buildLeadCoreFields in
   * crmSourcing.ts). Un lead inserito a mano non li valorizza. */
  city?: string;
  postalCode?: string;
  streetAddress?: string;
  website?: string;
  /** UID Zefix — presente solo per i lead creati dall'agente di sourcing
   * (functions/src/crmSourcing.ts), usato per deduplicare tra esecuzioni. */
  externalRegistryId?: string;
  /** Scopo sociale dichiarato — presente solo per i lead dell'agente e solo
   * se Zefix lo forniva. Letto anche da leadProfilingClassifier.ts (Fase 3)
   * per la classificazione AI sotto. */
  registryPurpose?: string;

  // Fase 3 (17/9/2026) — arricchimento AI automatico (Gemini + Google
  // Search grounding) alla creazione di un lead 'agente_registro' (v.
  // leadEnrichment.ts): trova il sito reale, valuta la probabilità di
  // profilazione dati (Art. 5 lett. f nLPD) ed estrae un contatto se
  // pubblicato sul sito. Tutti assenti finché l'arricchimento non è ancora
  // arrivato (di norma pochi secondi dopo la creazione) — un contatto può
  // restare assente anche dopo, se l'AI non trova un sito o un contatto
  // pubblicato.
  dataProfilingLikely?: boolean;
  dataProfilingConfidence?: number;
  dataProfilingReasoning?: string;
  dataProfilingAssessedAt?: unknown;

  // Contatto — assente per i lead appena trovati dall'agente finché
  // l'arricchimento AI non lo trova (o per sempre, se non pubblicato su
  // alcun sito). contactSource distingue un contatto trovato dall'AI (da
  // verificare prima di usarlo per un contatto commerciale reale) da uno
  // inserito o confermato da un umano.
  contactName?: string;
  contactRole?: string;
  contactSource?: 'ai_web_search';
  email?: string;
  phone?: string;
  preferredLanguage: PreferredLanguage;

  // Qualificazione
  painPoint?: string;
  hasDecisionAuthority?: boolean;
  estimatedTimeline?: EstimatedTimeline;
  currentSolution?: string;

  // Commerciale
  source: LeadSource;
  ownerId: string;
  estimatedDealValue?: number;

  // Pipeline / meta
  stage: LeadStage;
  stageHistory: StageHistoryEntry[];
  nextFollowUpAt?: unknown | null;
  lostReason?: LostReason;
  convertedMandateId?: string | null;
  onboardingToken?: string | null;

  createdAt: unknown;
  updatedAt: unknown;
  createdBy: string;
}

export type NewLeadInput = Omit<Lead, 'id' | 'stage' | 'stageHistory' | 'createdAt' | 'updatedAt' | 'createdBy' | 'convertedMandateId' | 'onboardingToken'>;

export interface LeadActivity {
  id: string;
  type: 'note' | 'call' | 'email' | 'meeting';
  text: string;
  createdAt: unknown;
  createdBy: string;
}

/** Task/promemoria concreto su un lead (es. "richiamare", "inviare
 * proposta") — a differenza di LeadActivity è mutabile (si segna fatto o si
 * elimina) e ha una scadenza. `leadId` è denormalizzato apposta: i task
 * "aperti" si leggono anche con una collectionGroup query cross-lead (v.
 * subscribeToOpenTasks in leadService.ts), dove risalire al lead genitore
 * dal solo path del documento sarebbe più fragile che leggere un campo. */
export interface LeadTask {
  id: string;
  leadId: string;
  text: string;
  dueAt: unknown; // data ISO
  done: boolean;
  createdAt: unknown;
  createdBy: string;
}

export type NewLeadTaskInput = Pick<LeadTask, 'text' | 'dueAt'>;
